import axios from 'axios'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { ELocalStorageKey } from '/@/types/enums'

const REFRESH_URL = '/manage/api/v1/token/refresh'
/** Refresh if the token expires within this window. */
const REFRESH_BUFFER_MS = 5 * 60 * 1000
/** Fallback periodic refresh for Pilot native API calls. */
const PERIODIC_CHECK_MS = 60 * 1000

type TokenRefreshHandler = (token: string) => void

let refreshPromise: Promise<string | null> | null = null
let onTokenRefreshed: TokenRefreshHandler | null = null
let periodicRefreshTimer: ReturnType<typeof setInterval> | null = null

function decodeTokenExpiry (token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) {
      return null
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(normalized))
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null
  } catch {
    return null
  }
}

function shouldRefreshToken (token: string): boolean {
  const expiry = decodeTokenExpiry(token)
  if (!expiry) {
    return false
  }
  return expiry - Date.now() <= REFRESH_BUFFER_MS
}

function persistToken (token: string): void {
  localStorage.setItem(ELocalStorageKey.Token, token)
  onTokenRefreshed?.(token)
}

export function setTokenRefreshHandler (handler: TokenRefreshHandler | null): void {
  onTokenRefreshed = handler
}

export async function refreshAccessToken (): Promise<string | null> {
  const currentToken = localStorage.getItem(ELocalStorageKey.Token)
  if (!currentToken) {
    return null
  }

  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = axios.post(
    REFRESH_URL,
    {},
    {
      baseURL: CURRENT_CONFIG.baseURL,
      headers: {
        'Content-Type': 'application/json',
        [ELocalStorageKey.Token]: currentToken,
      },
    },
  ).then(response => {
    const accessToken = response.data?.data?.access_token
    if (response.data?.code === 0 && accessToken) {
      persistToken(accessToken)
      return accessToken as string
    }
    return null
  }).catch(() => null).finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

export async function ensureValidToken (): Promise<string | null> {
  const token = localStorage.getItem(ELocalStorageKey.Token)
  if (!token) {
    return null
  }
  if (shouldRefreshToken(token)) {
    return refreshAccessToken()
  }
  return token
}

export function startPeriodicTokenRefresh (): void {
  stopPeriodicTokenRefresh()
  const tick = () => {
    const token = localStorage.getItem(ELocalStorageKey.Token)
    if (token && shouldRefreshToken(token)) {
      refreshAccessToken().catch(() => {})
    }
  }
  tick()
  periodicRefreshTimer = setInterval(tick, PERIODIC_CHECK_MS)
}

export function stopPeriodicTokenRefresh (): void {
  if (periodicRefreshTimer) {
    clearInterval(periodicRefreshTimer)
    periodicRefreshTimer = null
  }
}

export function isAuthExemptUrl (url?: string): boolean {
  if (!url) {
    return false
  }
  return url.includes('/login') || url.includes('/token/refresh')
}
